import { MultiPolygon, Polygon } from 'geojson';
import useSelectCityPmc from 'hooks/useSelectCityPmc';
import { useMemo } from 'react';
import { HidePolygons, useMapStore } from 'stores/mapStore';
import { Maybe, ParkZone } from 'types/generatedSchemaTypes';
import { PolygonItem } from 'types/MapTypes';
import { BoxDimensions, getBoxDimensionsFromRegion } from 'utils/mapUtils';
import { MapPolygons } from './MapDataRenderers';

type ParkZonePolygonsProps = {
  parkingZones: Maybe<ParkZone>[] | undefined;
  skipZoomLevel: boolean;
  withCity: boolean;
  withLTP: boolean;
};

/**
 * Type guard to check if a parking zone has a valid area
 * @param zone - Parking zone to check
 * @returns Boolean indicating if zone has valid area
 */
const hasArea = (zone: Maybe<ParkZone>): zone is ParkZone => {
  return Boolean(zone?.area);
};

/**
 * Creates a polygon representing the user's view box
 * @param outerBox - Dimensions of the outer box
 * @returns Polygon geometry representing the user's view box
 */
const createUsersViewBoxPolygon = (outerBox: BoxDimensions): Polygon => {
  return {
    type: 'Polygon',
    coordinates: [
      [
        [outerBox.bottomLeftLng, outerBox.bottomLeftLat],
        [outerBox.bottomLeftLng, outerBox.upperRightLat],
        [outerBox.upperRightLng, outerBox.upperRightLat],
        [outerBox.upperRightLng, outerBox.bottomLeftLat],
        [outerBox.bottomLeftLng, outerBox.bottomLeftLat],
      ],
    ],
  };
};

/**
 * Checks if two polygons intersect using their bounding boxes
 * @param userPolygon - Polygon representing user's view
 * @param areaPolygon - MultiPolygon representing parking zone area
 * @returns Boolean indicating if polygons intersect
 */
const doPolygonsIntersect = (
  userPolygon: Polygon,
  areaPolygon: MultiPolygon | Polygon
) => {
  const bbox1 = getBoundingBox(userPolygon);
  const bbox2 = getBoundingBox(areaPolygon);

  return !(
    bbox1.maxLng < bbox2.minLng ||
    bbox1.minLng > bbox2.maxLng ||
    bbox1.maxLat < bbox2.minLat ||
    bbox1.minLat > bbox2.maxLat
  );
};

/**
 * Filters a MultiPolygon to only include polygons that intersect with the user's view
 * @param userPolygon - Polygon representing the user's current view area
 * @param multiPolygon - MultiPolygon containing multiple polygon areas to filter
 * @returns A new MultiPolygon containing only the polygons that intersect with the user's view
 */
const filterIntersectingPolygons = (
  userPolygon: Polygon,
  multiPolygon: MultiPolygon
): MultiPolygon => {
  // Filter only the polygons that intersect with the user's view
  const filteredCoordinates = multiPolygon.coordinates.filter(
    (polygonCoords) => {
      // Create a temporary Polygon from this part of the MultiPolygon
      const singlePolygon: Polygon = {
        type: 'Polygon',
        coordinates: polygonCoords,
      };

      // Check if this individual polygon intersects with the user's view
      return doPolygonsIntersect(userPolygon, singlePolygon);
    }
  );

  return {
    type: 'MultiPolygon',
    coordinates: filteredCoordinates,
  };
};

const filterIfOptimizedMap = (
  hidePolygons: HidePolygons,
  item: PolygonItem
) => {
  return !(item.locationType === 'STREET'
    ? hidePolygons.street
    : hidePolygons.other);
};

/**
 * Calculates the bounding box for a polygon or multipolygon
 * @param polygon - Input polygon or multipolygon
 * @returns Object containing min/max coordinates of the bounding box
 */
const getBoundingBox = (polygon: MultiPolygon | Polygon) => {
  const coords =
    polygon.type === 'MultiPolygon'
      ? polygon.coordinates.flatMap((p) => p.flatMap((p) => p))
      : polygon.coordinates.flatMap((p) => p);

  const lngs = coords.map((c: number[]) => c[0]);
  const lats = coords.map((c: number[]) => c[1]);

  return {
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
};

/**
 * Component that renders parking zone polygons on a map
 * Filters and displays only the polygons that intersect with the current view
 * @param props - Component props containing parking zones data
 * @returns React component rendering filtered parking zone polygons
 */
const ParkZonePolygons: React.FC<ParkZonePolygonsProps> = ({
  parkingZones,
  skipZoomLevel,
  withCity,
  withLTP,
}) => {
  const hideContent = useMapStore((state) => state.optimizeMapContent);
  const regionCoords = useMapStore((state) => state.regionCoordinates);
  const { onItemSelect } = useSelectCityPmc();

  //List of all parking zones that have an area
  const polygonItems: PolygonItem[] = useMemo(() => {
    return (
      parkingZones?.filter(hasArea).map((zone) => ({
        uid: zone.uid,
        area: zone.area!,
        locationType: zone.locationType,
      })) ?? []
    );
  }, [parkingZones]);

  const activePolygons: PolygonItem[] = useMemo(() => {
    const { street, other } = hideContent;
    if (street && other) {
      return [];
    }
    const outerBox = getBoxDimensionsFromRegion(regionCoords).outerBox;
    const usersViewBoxPolygon = createUsersViewBoxPolygon(outerBox);

    return polygonItems
      .filter(
        (polygon) =>
          skipZoomLevel ||
          (doPolygonsIntersect(usersViewBoxPolygon, polygon.area) &&
            filterIfOptimizedMap(hideContent, polygon))
      )
      .map((polygon) => ({
        ...polygon,
        // Replace the original MultiPolygon with one containing only intersecting polygons
        area: filterIntersectingPolygons(usersViewBoxPolygon, polygon.area),
      }));
  }, [polygonItems, regionCoords, hideContent, skipZoomLevel]);

  return (
    <MapPolygons
      activePolygons={activePolygons}
      onItemSelect={withCity ? onItemSelect : undefined}
      withLTP={withLTP}
    />
  );
};
export default ParkZonePolygons;
